import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Container,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";

import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";

import {
  AddOrder,
  GetBankTransferDetails,
} from "../utils/order";

import { CLEAR_CART } from "../features/productInCartSlice";

import UserDetailsSection from "./UserDetailsSection";
import ShippingMethodSelector from "./ShippingMethodSelector";
import PaymentMethodSelector from "./PaymentMethodSelector";
import DealChip from "./DealChip";

import {
  getUnitPrice,
  calculateItemTotal,
} from "../helpers/cartPriceHelper";

import { showOrderSuccessAlert } from "../helpers/orderSuccessAlert";

import {
  calculateOrderDiscountSummary,
  formatILS,
} from "../helpers/orderDiscountHelper";

const focusStyle = {
  "&:focus-visible": {
    outline: "3px solid #111",
    outlineOffset: "3px",
  },
};

const cardStyle = {
  borderRadius: 3,
  border: "2px solid #D81B60",
};

const CheckoutSummary = () => {
  const loggedUser = useSelector(
    (state) => state.user.logedUser
  );

  const cart = useSelector(
    (state) => state.cart.items || []
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const shippingSectionRef = useRef(null);
  const paymentSectionRef = useRef(null);
  const termsSectionRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] =
    useState("");
  const [paymentMethod, setPaymentMethod] =
    useState("");
  const [acceptedTerms, setAcceptedTerms] =
    useState(false);
  const [errors, setErrors] = useState({});

  const orderForCalculation = {
    user: loggedUser,
    ordersProducts: cart,
  };

  const discountSummary =
    calculateOrderDiscountSummary(
      orderForCalculation
    );

  const total = discountSummary.finalTotal;

  const totalItems = cart.reduce(
    (sum, item) =>
      sum + Number(item?.amount || 0),
    0
  );

  const API_BASE =
    process.env.REACT_APP_API_BASE_URL || "";

  const resolveImgSrc = (img) => {
    if (!img) {
      return null;
    }

    if (img instanceof File) {
      return URL.createObjectURL(img);
    }

    const path = String(img).replace(/\\/g, "/");

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("//")
    ) {
      return path;
    }

    return `${API_BASE}${path}`;
  };

  const getCurrentDate = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(
      today.getMonth() + 1
    ).padStart(2, "0");
    const day = String(
      today.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getShippingAddress = () => {
    const address =
      loggedUser?.userAddresses?.[0];

    if (!address) {
      return "";
    }

    return [
      address.city,
      `${address.street || ""} ${
        address.houseNumber || ""
      }`.trim(),
      address.apartment
        ? `דירה ${address.apartment}`
        : "",
      address.floor
        ? `קומה ${address.floor}`
        : "",
      address.entrance
        ? `כניסה ${address.entrance}`
        : "",
      address.notes
        ? `הערות: ${address.notes}`
        : "",
    ]
      .filter(Boolean)
      .join(", ");
  };

  const validateCheckout = () => {
    const validationErrors = {};

    if (!shippingMethod) {
      validationErrors.shippingMethod =
        "יש לבחור שיטת משלוח";
    }

    if (!paymentMethod) {
      validationErrors.paymentMethod =
        "יש לבחור אמצעי תשלום";
    }

    if (!acceptedTerms) {
      validationErrors.acceptedTerms =
        "יש לאשר את תנאי השימוש ותקנון המשלוחים";
    }

    return validationErrors;
  };

  const focusFirstError = (
    validationErrors
  ) => {
    window.setTimeout(() => {
      if (
        validationErrors.shippingMethod
      ) {
        shippingSectionRef.current?.focus();
        return;
      }

      if (
        validationErrors.paymentMethod
      ) {
        paymentSectionRef.current?.focus();
        return;
      }

      if (
        validationErrors.acceptedTerms
      ) {
        termsSectionRef.current?.focus();
      }
    }, 100);
  };

  const handlePay = async () => {
    if (cart.length === 0) {
      await Swal.fire({
        title: "העגלה שלך ריקה",
        text: "יש להוסיף מוצרים לפני ביצוע הזמנה.",
        icon: "warning",
        confirmButtonText: "חזרה למוצרים",
        confirmButtonColor: "#D81B60",
      });

      navigate("/products");
      return;
    }

    const validationErrors =
      validateCheckout();

    if (
      Object.keys(validationErrors).length >
      0
    ) {
      setErrors(validationErrors);

      await Swal.fire({
        title: "חסרים פרטים",
        text: "יש להשלים את כל הפרטים ולאשר את תנאי השימוש.",
        icon: "warning",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });

      focusFirstError(validationErrors);
      return;
    }

    setErrors({});

    const data = {
      id: 0,
      userId: loggedUser.id,
      totalCost: total,
      date: getCurrentDate(),
      statusId: 3,
      shippingMethod,
      shippingAddress:
        getShippingAddress(),
      paymentMethod,

      ordersProducts: cart.map(
        (item) => ({
          id: 0,
          productId:
            item.productId ??
            item.product?.id,
          amount: item.amount,
          productOptionId:
            item.productOptionId ??
            item.productOption?.id ??
            null,
        })
      ),
    };

    try {
      setLoading(true);

      const response = await AddOrder(data);

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        const createdOrder =
          response.data;

        const orderId =
          createdOrder?.id;

        let bankDetails = null;

        if (
          paymentMethod ===
            "BankTransfer" &&
          orderId
        ) {
          try {
            bankDetails =
              await GetBankTransferDetails(
                orderId
              );
          } catch (error) {
            console.error(
              "שגיאה בקבלת פרטי הבנק:",
              error
            );
          }
        }

        await showOrderSuccessAlert({
          orderId,
          total,
          paymentMethod,
          bankDetails,
        });

        dispatch(CLEAR_CART());
        navigate("/");
      }
    } catch (error) {
      console.error(
        "שגיאה ביצירת ההזמנה:",
        error.response?.data || error
      );

      await Swal.fire({
        title: "שגיאה בשליחת ההזמנה",
        text:
          error.response?.data?.message ||
          "לא הצלחנו לשלוח את ההזמנה. נסי שוב בעוד רגע.",
        icon: "error",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!loggedUser) {
    return (
      <Box
        component="main"
        dir="rtl"
        sx={{
          mt: 10,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography
          component="h1"
          variant="h5"
          sx={{ mb: 2 }}
        >
          יש להתחבר כדי להמשיך לתשלום
        </Typography>

        <Button
          type="button"
          variant="contained"
          onClick={() =>
            navigate("/signIn")
          }
          sx={{
            minHeight: 44,
            backgroundColor: "#D81B60",

            "&:hover": {
              backgroundColor: "#ad1457",
            },

            ...focusStyle,
          }}
        >
          מעבר להתחברות
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      dir="rtl"
      aria-labelledby="checkout-title"
      sx={{
        minHeight: "100vh",
        py: {
          xs: 2,
          md: 5,
        },
      }}
    >
      <Container maxWidth="lg">
        <Box
          component="header"
          sx={{
            mb: 3,
            textAlign: "right",
          }}
        >
          <Typography
            id="checkout-title"
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 0.5,
            }}
          >
            השלמת הזמנה
          </Typography>

          <Typography
            component="p"
            color="text.secondary"
          >
            בדקי את פרטי ההזמנה ובחרי
            שיטת משלוח ואמצעי תשלום
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "360px minmax(0, 1fr)",
            },
            gap: 4,
            alignItems: "start",
            direction: "ltr",
          }}
        >
          {/* פרטי לקוח, משלוח ותשלום */}
          <Stack
            spacing={3}
            sx={{ direction: "rtl" }}
          >
            <Card
              component="section"
              aria-labelledby="customer-details-title"
              sx={{
                ...cardStyle,
                boxShadow:
                  "0 5px 24px rgba(0,0,0,0.06)",
              }}
            >
              <CardContent
                sx={{
                  p: {
                    xs: 2,
                    md: 3,
                  },
                }}
              >
                <Typography
                  id="customer-details-title"
                  component="h2"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  פרטי הלקוח וכתובת
                  למשלוח
                </Typography>

                <UserDetailsSection />
              </CardContent>
            </Card>

            <Card
              ref={shippingSectionRef}
              component="section"
              tabIndex={-1}
              aria-labelledby="shipping-title"
              aria-describedby={
                errors.shippingMethod
                  ? "shipping-error"
                  : undefined
              }
              sx={{
                ...cardStyle,

                "&:focus": {
                  outline:
                    "3px solid #111",
                  outlineOffset: "3px",
                },
              }}
            >
              <CardContent
                sx={{
                  p: {
                    xs: 2,
                    md: 3,
                  },
                }}
              >
                <Typography
                  id="shipping-title"
                  component="h2"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  שיטת משלוח
                </Typography>

                <ShippingMethodSelector
                  value={shippingMethod}
                  onChange={(newValue) => {
                    setShippingMethod(
                      newValue
                    );

                    setErrors(
                      (previous) => ({
                        ...previous,
                        shippingMethod: "",
                      })
                    );
                  }}
                  error={
                    Boolean(
                      errors.shippingMethod
                    )
                  }
                />

                {errors.shippingMethod && (
                  <Typography
                    id="shipping-error"
                    role="alert"
                    variant="body2"
                    color="error"
                    sx={{
                      mt: 1,
                      fontWeight: 600,
                    }}
                  >
                    {errors.shippingMethod}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card
              ref={paymentSectionRef}
              component="section"
              tabIndex={-1}
              aria-labelledby="payment-title"
              aria-describedby={
                errors.paymentMethod
                  ? "payment-error"
                  : undefined
              }
              sx={{
                ...cardStyle,

                "&:focus": {
                  outline:
                    "3px solid #111",
                  outlineOffset: "3px",
                },
              }}
            >
              <CardContent
                sx={{
                  p: {
                    xs: 2,
                    md: 3,
                  },
                }}
              >
                <Typography
                  id="payment-title"
                  component="h2"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  אמצעי תשלום
                </Typography>

                <PaymentMethodSelector
                  value={paymentMethod}
                  onChange={(newValue) => {
                    setPaymentMethod(
                      newValue
                    );

                    setErrors(
                      (previous) => ({
                        ...previous,
                        paymentMethod: "",
                      })
                    );
                  }}
                  error={
                    errors.paymentMethod
                  }
                />

                {errors.paymentMethod && (
                  <Typography
                    id="payment-error"
                    role="alert"
                    variant="body2"
                    color="error"
                    sx={{
                      mt: 1,
                      fontWeight: 600,
                    }}
                  >
                    {errors.paymentMethod}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>

          {/* סיכום ההזמנה */}
          <Card
            component="section"
            aria-labelledby="order-summary-title"
            sx={{
              ...cardStyle,
              direction: "rtl",
              position: {
                md: "sticky",
              },
              top: {
                md: 20,
              },
            }}
          >
            <CardContent
              sx={{
                p: {
                  xs: 2,
                  md: 3,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <ShoppingBagOutlinedIcon
                  aria-hidden="true"
                />

                <Typography
                  id="order-summary-title"
                  component="h2"
                  variant="h6"
                  sx={{ fontWeight: 800 }}
                >
                  סיכום ההזמנה
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {cart.length === 0 ? (
                <Typography
                  role="status"
                  sx={{
                    py: 3,
                    textAlign: "center",
                  }}
                >
                  אין מוצרים בעגלה
                </Typography>
              ) : (
                <Box
                  component="ul"
                  aria-label={`מוצרי ההזמנה, ${totalItems} יחידות`}
                  tabIndex={
                    cart.length > 7
                      ? 0
                      : undefined
                  }
                  sx={{
                    m: 0,
                    p: 0,
                    listStyle: "none",
                    maxHeight:
                      cart.length > 7
                        ? 700
                        : "none",
                    overflowY:
                      cart.length > 7
                        ? "auto"
                        : "visible",
                    pl:
                      cart.length > 7
                        ? 1
                        : 0,

                    "&:focus-visible": {
                      outline:
                        "3px solid #111",
                      outlineOffset: "3px",
                    },

                    "&::-webkit-scrollbar": {
                      width: "7px",
                    },

                    "&::-webkit-scrollbar-track":
                      {
                        backgroundColor:
                          "#f1f1f1",
                        borderRadius: "10px",
                      },

                    "&::-webkit-scrollbar-thumb":
                      {
                        backgroundColor:
                          "#757575",
                        borderRadius: "10px",
                      },
                  }}
                >
                  {cart.map(
                    (item, index) => {
                      const productName =
                        item.product?.name ||
                        "מוצר ללא שם";

                      const displayImage =
                        item.productOption
                          ?.image ||
                        item.product?.image;

                      const imageSrc =
                        resolveImgSrc(
                          displayImage
                        );

                      const unitPrice =
                        getUnitPrice(item);

                      const lineTotal =
                        calculateItemTotal(
                          item
                        );

                      const itemKey = `${
                        item.productId ??
                        item.product?.id ??
                        index
                      }-${
                        item.productOptionId ??
                        item.productOption
                          ?.id ??
                        "without-option"
                      }`;

                      const headingId =
                        `checkout-item-${index}`;

                      return (
                        <Box
                          component="li"
                          key={itemKey}
                        >
                          <Box
                            sx={{
                              display:
                                "flex",
                              alignItems:
                                "flex-start",
                              gap: 1.5,
                              py: 2,
                            }}
                          >
                            {imageSrc ? (
                              <Box
                                component="img"
                                src={imageSrc}
                                alt={`תמונת המוצר ${productName}`}
                                loading="lazy"
                                sx={{
                                  width: 68,
                                  height: 68,
                                  flexShrink: 0,
                                  objectFit:
                                    "contain",
                                  borderRadius: 2,
                                  border:
                                    "1px solid #eceff1",
                                }}
                              />
                            ) : (
                              <Box
                                role="img"
                                aria-label={`אין תמונה זמינה עבור ${productName}`}
                                sx={{
                                  width: 68,
                                  height: 68,
                                  flexShrink: 0,
                                  display:
                                    "flex",
                                  alignItems:
                                    "center",
                                  justifyContent:
                                    "center",
                                  borderRadius: 2,
                                  border:
                                    "1px solid #eceff1",
                                  backgroundColor:
                                    "grey.100",
                                  textAlign:
                                    "center",
                                  fontSize:
                                    "0.75rem",
                                }}
                              >
                                אין תמונה
                              </Box>
                            )}

                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  mt: 0.5,
                                }}
                              >
                                <DealChip
                                  product={
                                    item.product
                                  }
                                />
                              </Box>

                              <Typography
                                id={headingId}
                                component="h3"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 14,
                                  lineHeight: 1.4,
                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {productName}
                              </Typography>

                              {item.productOption && (
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0 }}
                                >
                                  {item
                                    .productOption
                                    .optionType ===
                                  "Color"
                                    ? "צבע"
                                    : "בחירה"}
                                  :{" "}
                                  {
                                    item
                                      .productOption
                                      .value
                                  }
                                </Typography>
                              )}

                              <Typography
                                component="p"
                                variant="body2"
                                color="text.secondary"
                                aria-label={`כמות ${item.amount}, מחיר ליחידה ${unitPrice.toFixed(
                                  2
                                )} שקלים`}
                                sx={{
                                  mt: 0.7,
                                  mb: 0,
                                }}
                              >
                                {item.amount} ×{" "}
                                <Box
                                  component="span"
                                  dir="ltr"
                                  sx={{
                                    display:
                                      "inline-block",
                                  }}
                                >
                                  {unitPrice.toFixed(
                                    2
                                  )}{" "}
                                  ₪
                                </Box>
                              </Typography>
                            </Box>

                            <Typography
                              component="p"
                              aria-label={`סך הכול עבור ${productName}: ${lineTotal.toFixed(
                                2
                              )} שקלים`}
                              sx={{
                                fontWeight: 700,
                                whiteSpace:
                                  "nowrap",
                                m: 0,
                              }}
                            >
                              <Box
                                component="span"
                                dir="ltr"
                                sx={{
                                  display:
                                    "inline-block",
                                }}
                              >
                                {lineTotal.toFixed(
                                  2
                                )}{" "}
                                ₪
                              </Box>
                            </Typography>
                          </Box>

                          {index <
                            cart.length -
                              1 && (
                            <Divider
                              aria-hidden="true"
                              sx={{
                                width: "100%",
                                borderColor:
                                  "#e0e0e0",
                              }}
                            />
                          )}
                        </Box>
                      );
                    }
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2.5 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  component="span"
                  color="text.secondary"
                >
                  מספר מוצרים
                </Typography>

                <Typography
                  component="span"
                  aria-label={`${totalItems} יחידות בסך הכול`}
                >
                  {totalItems}
                </Typography>
              </Box>

              {discountSummary.isEligible &&
                discountSummary.discountAmount >
                  0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        component="span"
                        color="text.secondary"
                      >
                        סה״כ לפני הנחת
                        לקוח
                      </Typography>

                      <Typography
                        component="span"
                        color="text.secondary"
                      >
                        {formatILS(
                          discountSummary.subtotal
                        )}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color:
                            "success.dark",
                          fontWeight: 700,
                        }}
                      >
                        הנחת לקוח 5%
                      </Typography>

                      <Typography
                        component="span"
                        sx={{
                          color:
                            "success.dark",
                          fontWeight: 700,
                        }}
                      >
                        {formatILS(
                          discountSummary.discountAmount
                        )}
                      </Typography>
                    </Box>
                  </>
                )}

              <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Typography
                  component="span"
                  variant="h6"
                  sx={{ fontWeight: 800 }}
                >
                  סה״כ לתשלום
                </Typography>

                <Typography
                  component="span"
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: "#AD1457",
                  }}
                >
                  {formatILS(
                    discountSummary.finalTotal
                  )}
                </Typography>
              </Box>

              {/* אישור תנאים */}
              <Box
                ref={termsSectionRef}
                tabIndex={-1}
                aria-describedby={
                  errors.acceptedTerms
                    ? "terms-error"
                    : undefined
                }
                sx={{
                  mt: 3,
                  direction: "rtl",

                  "&:focus": {
                    outline:
                      "3px solid #111",
                    outlineOffset: "3px",
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(event) => {
                        const checked =
                          event.target.checked;

                        setAcceptedTerms(
                          checked
                        );

                        if (checked) {
                          setErrors(
                            (previous) => ({
                              ...previous,
                              acceptedTerms:
                                "",
                            })
                          );
                        }
                      }}
                      inputProps={{
                        "aria-invalid":
                          Boolean(
                            errors.acceptedTerms
                          ),
                        "aria-describedby":
                          errors.acceptedTerms
                            ? "terms-error"
                            : undefined,
                      }}
                      sx={{
                        color:
                          errors.acceptedTerms
                            ? "error.main"
                            : "#616161",

                        "&.Mui-checked": {
                          color: "#D81B60",
                        },

                        ...focusStyle,
                      }}
                    />
                  }
                  label={
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        lineHeight: 1.8,
                        textAlign: "right",
                      }}
                    >
                      אני מאשר/ת שקראתי
                      והסכמתי ל־
                      <Box
                        component={Link}
                        to="/conditions"
                        onClick={(event) =>
                          event.stopPropagation()
                        }
                        sx={{
                          color: "#AD1457",
                          fontWeight: 700,
                          textDecoration:
                            "underline",
                          borderRadius: 0.5,

                          ...focusStyle,
                        }}
                      >
                        תנאי השימוש
                      </Box>
                      {" "}ול־{" "}
                      <Box
                        component={Link}
                        to="/shipping-policy"
                        onClick={(event) =>
                          event.stopPropagation()
                        }
                        sx={{
                          color: "#AD1457",
                          fontWeight: 700,
                          textDecoration:
                            "underline",
                          borderRadius: 0.5,

                          ...focusStyle,
                        }}
                      >
                        תקנון המשלוחים
                      </Box>
                    </Typography>
                  }
                  sx={{
                    m: 0,
                    width: "100%",
                    alignItems: "flex-start",

                    "& .MuiFormControlLabel-label":
                      {
                        flex: 1,
                        pt: 0.6,
                      },
                  }}
                />

                {errors.acceptedTerms && (
                  <Typography
                    id="terms-error"
                    role="alert"
                    variant="body2"
                    color="error"
                    sx={{
                      mt: 0.5,
                      mr: 4.5,
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    {errors.acceptedTerms}
                  </Typography>
                )}
              </Box>

              <Button
                type="button"
                fullWidth
                variant="contained"
                size="large"
                onClick={handlePay}
                disabled={loading}
                aria-busy={loading}
                aria-label={
                  loading
                    ? "ההזמנה נשלחת כעת"
                    : "אישור ושליחת ההזמנה"
                }
                sx={{
                  mt: 2,
                  minHeight: 52,
                  borderRadius: 2,
                  fontWeight: 800,
                  fontSize: 16,
                  backgroundColor:
                    "#D81B60",

                  "&:hover": {
                    backgroundColor:
                      "#ad1457",
                  },

                  "&.Mui-disabled": {
                    backgroundColor:
                      "#9e9e9e",
                    color: "#fff",
                  },

                  ...focusStyle,
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={22}
                      aria-hidden="true"
                      sx={{
                        color: "#fff",
                        ml: 1,
                      }}
                    />

                    <Box
                      component="span"
                      role="status"
                      aria-live="polite"
                    >
                      שולח הזמנה...
                    </Box>
                  </>
                ) : (
                  "אישור ושליחת ההזמנה"
                )}
              </Button>

              <Button
                type="button"
                fullWidth
                variant="text"
                startIcon={
                  <ArrowBackIcon
                    aria-hidden="true"
                  />
                }
                onClick={() =>
                  navigate("/cart")
                }
                disabled={loading}
                sx={{
                  mt: 1,
                  minHeight: 44,
                  color: "#37474f",

                  ...focusStyle,
                }}
              >
                חזרה לעגלת הקניות
              </Button>

              <Paper
                component="aside"
                aria-label="אבטחת הפרטים"
                elevation={0}
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor:
                    "#f6f8fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "center",
                  gap: 1,
                }}
              >
                <LockIcon
                  aria-hidden="true"
                  sx={{
                    fontSize: 18,
                    color: "success.dark",
                  }}
                />

                <Typography
                  component="p"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textAlign: "center",
                    m: 0,
                  }}
                >
                  ההזמנה והפרטים שלך
                  נשמרים בצורה מאובטחת
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default CheckoutSummary;